<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { computed, useSlots } from 'vue'

import LoadingIcon from '../../assets/icons/loading.svg?component'

type ButtonVariant = 'primary' | 'secondary' | 'outlined' | 'text' | 'transparent' | 'danger' | 'success' | 'accent'
type ButtonSize = 'default' | 'small' | 'big'
type Class = string | Record<string, boolean> | (string | Record<string, boolean>)[]

const props = withDefaults(
  defineProps<{
    variant?: ButtonVariant
    size?: ButtonSize
    type?: 'button' | 'submit'
    href?: string
    target?: string
    to?: string
    isLoading?: boolean
    isDisabled?: boolean
    isIcon?: boolean
    iconColor?: string
    iconSize?: number | string
    class?: Class
    alt?: string
  }>(),
  {
    variant: 'primary',
    size: 'default',
    type: 'button',
    href: undefined,
    isIcon: false,
    isLoading: false,
    target: undefined,
    isDisabled: false,
    to: undefined,
    iconSize: 24,
  },
)
defineSlots<{ default(): void; iconLeft(): void; iconRight(): void }>()
const slots = useSlots()
const height = computed(() => {
  if (props.size === 'small') {
    return '16px'
  }

  return '40px'
})
const computedProps = computed<{ is: string }>(() => {
  const classes = [
    {
      button: true,
      [props.variant]: true,
      ['is-link']: props.href,
      ['is-icon']: props.isIcon,
      disabled: props.isDisabled,
      loading: props.isLoading,
      ['default-size']: props.size === 'default',
      ['small']: props.size === 'small',
      ['big']: props.size === 'big',
    },
    props.class,
  ]
  if (props.href) {
    return {
      is: 'a',
      class: classes,
      href: props.href,
      target: props.target,
    }
  }
  if (props.to) {
    return {
      is: 'router-link',
      class: classes,
      to: props.to,
    }
  }
  return {
    is: 'button',
    class: classes,
    type: props.type,
    disabled: props.isDisabled || props.isLoading,
  }
})
</script>

<template>
  <component :is="computedProps.is" v-bind="computedProps" :aria-busy="isLoading ? 'true' : undefined">
    <slot name="iconLeft" />
    <ElIcon v-if="isIcon" :size="iconSize" :color="iconColor">
      <slot />
    </ElIcon>
    <slot v-else />
    <slot v-if="slots.iconRight && !isLoading" name="iconRight" />
    <ElIcon v-if="isLoading" :size="iconSize" class="button__spinner" aria-hidden="true">
      <LoadingIcon />
    </ElIcon>
  </component>
</template>

<style scoped lang="scss">
.button {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  min-height: v-bind(height);
  padding: 8px 24px;
  border: 1px solid var(--primary);
  border-radius: 40px;

  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  text-decoration: none;
  color: var(--primary);

  &__spinner {
    position: absolute;
  }

  transition: all var(--transition) ease;
  cursor: pointer;

  &.primary {
    background: var(--primary);
    color: var(--inverted);
  }

  &.primary:disabled,
  &.primary.disabled {
    background: var(--surface-disabled);
    border-color: var(--surface-disabled);
    color: var(--secondary-disabled);

    :deep(svg path) {
      fill: var(--secondary-disabled);
    }
  }

  &.secondary {
    background: var(--secondary-12);
    border-color: var(--secondary-12);
  }

  &.danger {
    background: var(--error);
    border-color: var(--error);
    color: var(--inverted);

    :deep(svg) {
      & path {
        fill: var(--inverted);
      }
    }
  }

  &.success {
    background: var(--success);
    border-color: var(--success);
    color: var(--inverted);

    :deep(svg) {
      & path {
        fill: var(--inverted);
      }
    }
  }

  &.outlined {
    background: var(--inverted);
  }

  &.transparent {
    background: rgba(255, 255, 255, 0.12);
    border: none;
    color: var(--inverted);
  }

  &.accent {
    background-image: linear-gradient(180deg, var(--yellow) 0%, var(--pink) 50%, var(--accent) 50%, var(--accent) 100%);
    background-position: 0 100%;
    background-repeat: no-repeat;
    background-size: 100% 200%;
    color: var(--primary);
    border-color: var(--accent);
    transition:
      background-position var(--transition) ease,
      border-color var(--transition) ease;

    &:hover,
    &:active {
      border-color: transparent;
      background-position: 0 0;
    }

    &:disabled {
      background: var(--surface-disabled);
      border-color: var(--surface-disabled);
      color: var(--secondary-disabled);
    }
  }

  &.disabled {
    color: rgba(255, 255, 255, 0.48);

    :deep(svg) {
      & path {
        fill: rgba(255, 255, 255, 0.48);
      }
    }
  }

  &.text {
    background: transparent;
    border: none;

    &.disabled {
      color: initial;
      opacity: 0.5;
    }

    &:hover {
      background: rgba(34, 34, 34, 0.12);
    }
  }

  &.is-icon {
    border: none;
  }

  &.default-size {
    padding: 8px 24px;
    height: 40px;

    &.is-icon {
      padding: 8px;
    }
  }

  &.small {
    padding: 0;
    height: 16px;

    &.is-icon {
      padding: 0;
    }
  }

  &.big {
    padding: 0;
    height: 56px;

    &.is-icon {
      height: 48px;
      padding: 0;
    }
  }

  &.disabled {
    cursor: default;
  }

  &.loading {
    color: transparent;

    :deep(*:not(.button__spinner):not(.button__spinner *)) {
      visibility: hidden;
    }
  }
}
</style>
